import axios from 'axios';
import { R7E_AuthData } from '../r7e_auth_data';

const API_CMS_URL = process.env.VUE_APP_API_CMS_URL;
const WEB_CMS_URL = new URL(API_CMS_URL).origin;
export const LocalStoragePrefix = process.env.VUE_APP_R7E_PREFIX || 'r7e.';
export const DEBUG_MODE = process.env.VUE_APP_DEBUG_MODE === "true";
const R7E_AVAILABLE_ROLES = ['publisher', 'author', 'consumer'];

class R7E_CmsAuth {
    authData = new R7E_AuthData();
    refreshTimer= null;
    observers = new Set();
    apiCMS_Client = axios.create({ baseURL: API_CMS_URL });
    availableRoles = R7E_AVAILABLE_ROLES;
    isNotifying = false;

    constructor() {
        this.apiCMS_Client.interceptors.request.use(config => {
            if (this.authData.token) {
                config.headers.Authorization = `Bearer ${this.authData.token}`;
            }
            return config;
        }, error => Promise.reject(error));
    }

    notifyObservers(d) {
        if (this.isNotifying) return;
        this.isNotifying = true;
        if (DEBUG_MODE) {
            console.log("R7E_CmsAuth.notifyObservers", d);       
        }
        this.observers.forEach(observer => observer({ data: d }));
        this.isNotifying = false;
    }
    registerCredentialsObserver(callback) {
        this.observers.add(callback);
        callback({ data: this.authData });
        return () => this.observers.delete(callback);
    }
    unregisterCredentialsObserver(callback) {
        this.observers.delete(callback);
    }
    decodeJWT(token) {
        if (!token) return null;
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        try {
            return JSON.parse(atob(parts[1]));
        } catch (e) {
            return null;
        }
    }
    scheduleTokenRefresh() {
        // console.log('scheduleTokenRefresh');
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        const payload = this.decodeJWT(this.authData.token);
        if (!payload || !payload.exp || !payload.iat) {
            console.error('refreshToken request: no payload or exp or iat!');
            return;
        }
        const now = Math.floor(Date.now() / 1000);
        const midLife = payload.iat + Math.floor((payload.exp - payload.iat) / 2);
        let delay = (midLife - now) * 1000;
        if (delay < 0) delay = 0;
        this.refreshTimer = setTimeout(async () => {
            try {
                await this.refreshToken();
            } catch (e) {
                console.error("refreshToken failed, clearing credentials");
                await this.clearCredentials();
            }
        }, delay);
    }
    async login(userInput, pass, condition = null) {
        const basicAuth = `Basic ${btoa(`${userInput}:${pass}`)}`;
        const client = axios.create({
            baseURL: API_CMS_URL,
            headers: { 'Authorization': basicAuth }
        });
        this.authData.roles = []

        try {
            const loginResponse = await client.get('/');
            // console.log("loginResponse", loginResponse);
            const userData = loginResponse.data;
            // console.log("userData", userData);
            let roles = [];

            for (const role of this.availableRoles) {
                if (userData[`is_${role}`]) {
                    roles.push(role);
                }
            }
            this.authData.roles = roles
            
            if (condition && typeof condition === 'function') {
                const shouldContinue = condition(userData);
                if (!shouldContinue) {
                    throw new Error("Login condition not met");
                }
            }
            // console.log("userData", userData);
            let jwtLink = null;
            if (userData['_links'] && userData['_links']['jwt:access_token']) { 
                jwtLink = userData['_links']['jwt:access_token'];
            } else {
                throw new Error("Authentication failed.");
            }
            const tokenResponse = await client.post(jwtLink.href);
            // console.log("tokenResponse", tokenResponse);
            const { access_token, refresh_key } = tokenResponse.data;
            this.authData.username = tokenResponse.data?.user?.name || userInput;
            this.authData.token = access_token;
            if (tokenResponse.data.user) {
                this.authData.userid = tokenResponse.data.user.uid; 
            } else {
                this.authData.userid = null;
            }
            this.authData.refreshKey = refresh_key;

            this.authData.roles = roles
            // this.updateAccountStorage(this.authData);  

            this.scheduleTokenRefresh();
            return this.authData.userid;
        } catch (error) {
            console.error("Login failed:", error.response ? error.response.data : error.message);
            throw error;
        }
    }

    async loginAccountWithMagicLink(uid, username, token, refresh) {
        if (DEBUG_MODE) {
            console.log("loginAccountWithMagicLink", uid, username, token, refresh);
        }
        this.authData.username = username;
        this.authData.name = null;
        this.authData.email = null;
        this.authData.token = token;
        this.authData.userid = uid;
        this.authData.refreshKey = refresh;
        this.authData.roles = []; // ["publisher", ...];
        this.authData.pendingRegistrations = {};

        this.updateAccountStorage(this.authData);
        // this.refreshToken();
        // this.scheduleTokenRefresh();

        return this.authData.userid;
    }

    updateAccountStorage(obj = null, profile = null) {
        if (!obj) {
            // console.log("updateAccountStorage, obj is null, using this.authData");
            obj = this.authData;
        }
        // console.log("updateAccountStorage, obj =", obj, this.authData);
        // console.log("updateAccountStorage, profile =", profile);
        try {

            // console.trace();
            let changed = false;

            if (localStorage.getItem(LocalStoragePrefix + 'username') !== obj.username) {
                localStorage.setItem(LocalStoragePrefix + 'username', obj.username);
                changed = true;
            }
            if (profile && profile.name) {
                localStorage.setItem(LocalStoragePrefix + 'name', profile.name);
                changed = true;
            } else if (obj.name) {
                localStorage.setItem(LocalStoragePrefix + 'name', obj.name);
                changed = true;
            }
            if (localStorage.getItem(LocalStoragePrefix + 'token') !== obj.token) {
                localStorage.setItem(LocalStoragePrefix + 'token', obj.token);
                changed = true;
            }        
            if (localStorage.getItem(LocalStoragePrefix + 'userid') !== obj.userid) {
                localStorage.setItem(LocalStoragePrefix + 'userid', obj.userid);
                changed = true;
            }
            if (localStorage.getItem(LocalStoragePrefix + 'refreshKey') !== obj.refreshKey) {
                localStorage.setItem(LocalStoragePrefix + 'refreshKey', obj.refreshKey);
                changed = true;
            }
            const rolesStr = JSON.stringify(obj.roles);
            if (localStorage.getItem(LocalStoragePrefix + 'roles') !== rolesStr) {
                localStorage.setItem(LocalStoragePrefix + 'roles', rolesStr);
                changed = true;
            }
            const pendingRegistrationsStr = JSON.stringify(obj.pendingRegistrations);
            if (localStorage.getItem(LocalStoragePrefix + 'pendingRegistrations') !== pendingRegistrationsStr) {
                localStorage.setItem(LocalStoragePrefix + 'pendingRegistrations', pendingRegistrationsStr);
                changed = true;
            }

            if (changed) {
                if (DEBUG_MODE) {
                    console.log("localStorage updated", localStorage, obj);
                }
                this.notifyObservers(obj);
            } else {
                if (DEBUG_MODE) {
                    console.log("LocalStorage NOT changed", obj);
                }
            }
        } catch (e) {
            console.error("updateAccountStorage failed", e);
        }
    }
    
    updateAccountRoles(roles) {
        this.authData.roles = roles;
        localStorage.setItem(LocalStoragePrefix + 'roles', JSON.stringify(roles));
    }
    updateAccountPendingRegistrations(pendingRegistrations) {
        this.authData.pendingRegistrations = pendingRegistrations;
        localStorage.setItem(LocalStoragePrefix + 'pendingRegistrations', JSON.stringify(pendingRegistrations));
    }

    async getAccountInfoFromResponseLinks(a_response) {
        let d = {};
        if (a_response['_links']) {
            let l_acc = a_response['_links']['account']
            if (l_acc && l_acc.href) {
                let l_acc_href = l_acc.href;
                const acc_response = await this.apiCMS_Client.get(l_acc_href);
                delete acc_response.data._links;
                Object.assign(d, acc_response.data);
            }
        }
        return d;
    }
    loadCredentials() {
        // console.log("R7E_CmsAuth.loadCredentials");
        this.authData.roles = []
        this.authData.username = localStorage.getItem(LocalStoragePrefix + 'username') || '';
        this.authData.name = localStorage.getItem(LocalStoragePrefix + 'name') || '';
        this.authData.roles = JSON.parse(localStorage.getItem(LocalStoragePrefix + 'roles') || '[]');
        if (this.authData.roles && typeof this.authData.roles === 'object' && !Array.isArray(this.authData.roles)) {
            // Convert roles object to array of keys with value true
            // Backward compatibility with old roles object
            this.authData.roles = Object.keys(this.authData.roles).filter(k => this.authData.roles[k]);
        }

        this.authData.token = localStorage.getItem(LocalStoragePrefix + 'token') || '';
        this.authData.userid = localStorage.getItem(LocalStoragePrefix + 'userid') || '';
        this.authData.refreshKey = localStorage.getItem(LocalStoragePrefix + 'refreshKey') || '';
        this.authData.pendingRegistrations = JSON.parse(localStorage.getItem(LocalStoragePrefix + 'pendingRegistrations') || '{}');
        // console.log('R7E_CmsAuth, loadCredentials', this.authData);
        this.notifyObservers(this.authData);
        if (this.authData.token) this.scheduleTokenRefresh();
        return !!(this.authData.token);
    }
    isAuthenticated() {
        // console.log('R7E_CmsAuth, isAuthenticated', this.authData.token);
        return !!this.authData.token;
    }
    hasPendingRegistrations() {
        return Object.keys(this.authData.pendingRegistrations).length > 0;
    }
    hasPendingRegistration(srv) {
        // console.log('R7E_CmsAuth, hasPendingRegistration', srv, this.authData.pendingRegistrations, this.authData.pendingRegistrations[srv]);
        return this.authData.pendingRegistrations[srv] || false;
    }

    hasAccessToService(service) {
        // console.log('R7E_CmsAuth, hasAccessToService', this.roles, service, this.roles.includes(service));
        return this.authData.roles.includes(service);
    }

    async refreshToken() {
            // Clear any existing refresh timer
        if (DEBUG_MODE) {
            console.log("R7E_CmsAuth.refreshToken", this.authData.userid, this.authData.refreshKey);
        }
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }

        if (!this.authData.userid || !this.authData.refreshKey) throw new Error('No refresh key or user available');
        const refreshEndpoint = `/user/${this.authData.userid}/jwt_access_token`;
        try {
            const formData = new FormData();
            formData.append('refresh', this.authData.refreshKey);
            formData.append('token', this.authData.token);
            // console.log("R7E_CmsAuth.refreshToken", refreshEndpoint, formData);
            const response = await this.apiCMS_Client.post(refreshEndpoint, formData);

            const { access_token, refresh_key } = response.data;
            if (access_token) { this.authData.token = access_token; }
            if (refresh_key) { this.authData.refreshKey = refresh_key; }
            this.updateAccountStorage(this.authData);
            this.scheduleTokenRefresh();
            return this.authData.token;
        } catch (error) {
            console.error("refreshToken failed", error);
            throw error;
        }
    }
    async refreshTokenIfNeeded() {
        const payload = this.decodeJWT(this.authData.token);
        if (!payload || !payload.exp || !payload.iat) return;
        const now = Math.floor(Date.now() / 1000);
        const reftime = payload.iat + Math.floor((payload.exp - payload.iat) / 8);
        if (now >= reftime) {
            await this.refreshToken();
        }
    }
    async revokeToken() {
        // console.log('R7E_CmsAuth, revokeToken', this.authData.userid, this.authData.token);
        if (!this.authData.userid || !this.authData.token) return;
        const revokeEndpoint = `/user/${this.authData.userid}/jwt_access_token`;
        const formData = new FormData();
        formData.append('op', 'discard');
        formData.append('token', this.authData.token);
        try {
            await this.apiCMS_Client.post(revokeEndpoint, formData);
        } catch (e) { /* Ignore errors on revoke */ }
    }

    logoutAccountSync() {
        this.clearCredentialsSync();
    }    
    clearCredentialsSync() {
        // console.log('R7E_CmsAuth, clearCredentialsSync', this.baseAppType);
        // console.trace();
        this.authData.clear();
        const authKeys = this.authData.getKeys();
        authKeys.forEach(key => {
            // console.log('clearCredentialsSync', LocalStoragePrefix + key);
            localStorage.removeItem(LocalStoragePrefix + key);
        });
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        console.log('R7E_CmsAuth, clearCredentialsSync done', this.authData);
        this.notifyObservers(this.authData);
    }
    async logoutAccount() {
        await this.clearCredentials();
    }    
    async clearCredentials() {
        // console.log('R7E_CmsAuth, clearCredentials callstack:');
        // console.trace();
        await this.revokeToken();
        this.clearCredentialsSync();
    }
    
    has_role(role) {
        return this.authData.roles.includes(role);
    }
    getUsername() {
        return localStorage.getItem(LocalStoragePrefix + 'username') || this.authData.username;
    }
    getDisplayName() {
        return localStorage.getItem(LocalStoragePrefix + 'name') || this.authData.name;
    }
    getRoles() {
        return this.authData.roles;
    }
    getToken() {
        return this.authData.token;
    }
    getRefreshKey() {
        return this.authData.refreshKey;
    }
    getUserid() {
        return this.authData.userid || null;
    }
    getPendingRegistrations() {
        return this.authData.pendingRegistrations;
    }
    getAuthData() {
        return this.authData;
    }
    getCMSUrl(v=null) {
        let url = WEB_CMS_URL;
        if (v) {
            if (url.endsWith('/') || v.startsWith('/')) {
                url = url + v;
            } else {
                url = url + '/' + v;
            }
        }
        return url;
    }

    /****************************************
     * Invitation and waiting list settings *
     ****************************************/

    /**
     * Check if an invitation code is valid.
     * @param {string} code - The invitation code to validate
     * @param {string} [product] - Product name (publisher, aggregator, author, recognyze). Uses current app context if not provided.
     * @returns {Promise<{valid: boolean, product?: string, email?: string, used?: boolean, used_at?: string, created_at?: string, expires_at?: string, max_uses?: number, use_count?: number}>}
     */
    async checkInvitationCode(code, product) { 
        const response = await this.apiCMS_Client.get(`invitations/code/${encodeURIComponent(code)}/`, { params: { product: product } });
        return response.data;
    }
    /**
     * Subscribe an email to the waiting list.
     * @param {string} email - Email to subscribe
     * @param {string} product - Product name (publisher, aggregator, author, recognyze). Required.
     * @returns {Promise<{subscribed: boolean, order?: number, total?: number, email?: string}>}
     */
    async subscribeToWaitingList(email, product) { 
        const response = await this.apiCMS_Client.post(`invitations/wait/${encodeURIComponent(email)}/`, { email: email, product: product });
        return response.data;
    }    

}

export { R7E_CmsAuth };
